import React from 'react';
import { cn } from '../../lib/utils';
import { icons } from './Icons';

const renderCellValue = (value: unknown): React.ReactNode => {
  if (React.isValidElement(value)) return value;
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') return value.toString();
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

export interface TableColumn<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  className?: string;
  headerClassName?: string;
  width?: string;
}

export interface TableProps<T> extends React.TableHTMLAttributes<HTMLTableElement> {
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  rowClick?: (item: T) => void;
  rowActionLabel?: string | ((item: T) => string);
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  emptyAction?: React.ReactNode;
  error?: React.ReactNode;
  errorMessage?: string;
  onRetry?: () => void;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  caption?: React.ReactNode;
  containerClassName?: string;
}

export function Table<T>({
  columns,
  data,
  keyExtractor,
  rowClick,
  rowActionLabel,
  loading = false,
  loadingMessage = 'Daten werden geladen...',
  emptyMessage = 'Keine Daten verfügbar',
  emptyAction,
  error,
  errorMessage,
  onRetry,
  striped = true,
  hoverable = true,
  compact = true,
  caption,
  className,
  containerClassName,
  ...props
}: TableProps<T>) {
  const cellPadding = compact ? 'px-3 py-2.5 sm:px-4' : 'px-4 py-3 sm:px-5';
  const errorContent = error ?? errorMessage ?? 'Daten konnten nicht geladen werden.';
  const colSpan = Math.max(columns.length, 1);

  return (
    <div className={cn('overflow-x-auto rounded-md border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900', containerClassName)}>
      <table
        className={cn('w-full border-collapse text-sm text-neutral-900 dark:text-neutral-100', className)}
        aria-busy={loading || undefined}
        {...props}
      >
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className="border-b border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800/60">
          <tr>
            {columns.map(column => (
              <th
                key={column.key}
                scope="col"
                className={cn(
                  `${cellPadding} text-left align-middle text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400`,
                  column.headerClassName
                )}
                style={{ width: column.width }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
          {error || errorMessage ? (
            <tr>
              <td colSpan={colSpan} className={cellPadding}>
                <div className="flex min-h-[120px] flex-col items-center justify-center gap-3 px-4 text-center" role="alert">
                  <icons.AlertCircle className="h-5 w-5 text-emergency-600 dark:text-emergency-400" aria-hidden="true" />
                  <div className="max-w-md text-sm text-neutral-700 dark:text-neutral-300">{errorContent}</div>
                  {onRetry && (
                    <button
                      type="button"
                      onClick={onRetry}
                      className="inline-flex h-8 items-center rounded-md border border-emergency-600 px-3 text-xs font-medium text-emergency-700 transition-colors hover:bg-emergency-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emergency-500 focus-visible:ring-offset-2 dark:border-emergency-400 dark:text-emergency-400 dark:hover:bg-emergency-900/20 dark:focus-visible:ring-offset-neutral-900"
                    >
                      Erneut versuchen
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ) : loading ? (
            <tr>
              <td colSpan={colSpan} className={cellPadding}>
                <div className="flex min-h-[120px] items-center justify-center gap-3 text-sm text-neutral-500 dark:text-neutral-400" role="status" aria-live="polite">
                  <icons.Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  <span>{loadingMessage}</span>
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className={cellPadding}>
                <div className="flex min-h-[120px] flex-col items-center justify-center gap-3 px-4 text-center" aria-live="polite">
                  <icons.Inbox className="h-5 w-5 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">{emptyMessage}</p>
                  {emptyAction}
                </div>
              </td>
            </tr>
          ) : (
            data.map((item, rowIndex) => {
              const rowLabel = typeof rowActionLabel === 'function' ? rowActionLabel(item) : rowActionLabel;
              return (
                <tr
                  key={keyExtractor(item)}
                  className={cn(
                    striped && rowIndex % 2 === 0 && 'bg-neutral-50/60 dark:bg-neutral-800/30',
                    hoverable && rowClick && 'cursor-pointer transition-colors hover:bg-primary-50 dark:hover:bg-primary-900/20'
                  )}
                  tabIndex={rowClick ? 0 : undefined}
                  aria-label={rowClick ? rowLabel : undefined}
                  onClick={
                    rowClick
                      ? event => {
                          if ((event.target as HTMLElement).closest('button, a, input, select, textarea, [role="button"]')) return;
                          rowClick(item);
                        }
                      : undefined
                  }
                  onKeyDown={
                    rowClick
                      ? event => {
                          if ((event.target as HTMLElement).closest('button, a, input, select, textarea, [role="button"]')) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            rowClick(item);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map(column => (
                    <td
                      key={column.key}
                      className={cn(`${cellPadding} align-middle text-sm text-neutral-800 dark:text-neutral-100`, column.className)}
                    >
                      {column.render ? column.render(item) : renderCellValue((item as Record<string, unknown>)[column.key])}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
